package com.arena_web.ui.sections.dashboard

import androidx.compose.runtime.*
import com.arena_web.data.dto.*
import com.arena_web.ui.components.cards.*
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.modal.BookingDetailModal
import com.arena_web.ui.components.modal.ModalAction
import com.arena_web.ui.components.widgets.*
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// =============================================================================
// Helpers: map BookingDto to UI enums
// =============================================================================

private fun mapBookingField(fieldId: String, fields: List<FieldDto>): BookingField {
    val idx = fields.indexOfFirst { it.id == fieldId }
    return when (idx) {
        0 -> BookingField.Field01
        1 -> BookingField.Field02
        2 -> BookingField.Field03
        else -> BookingField.Field01
    }
}

private fun mapPaymentStatus(booking: BookingDto): PaymentStatus {
    if (booking.isPaid) return PaymentStatus.Paid
    return when (booking.status.uppercase()) {
        "CONFIRMED" -> PaymentStatus.Confirmed
        "COMPLETED" -> PaymentStatus.Paid
        "PENDING" -> PaymentStatus.Pending
        "UNPAID" -> PaymentStatus.Unpaid
        else -> PaymentStatus.Pending
    }
}

private fun paymentStatusToModalAction(status: PaymentStatus): ModalAction = when (status) {
    PaymentStatus.Pending -> ModalAction.Approval
    PaymentStatus.Confirmed, PaymentStatus.Paid -> ModalAction.Complete
    PaymentStatus.Unpaid -> ModalAction.Action
}

private fun paymentStatusToBookingStatus(status: PaymentStatus): String = when (status) {
    PaymentStatus.Confirmed, PaymentStatus.Paid -> "Completed"
    PaymentStatus.Pending -> "Upcoming"
    PaymentStatus.Unpaid -> "In Progress"
}

private val timeSlots = (7..23).map { hour -> "${hour}:00" }

// =============================================================================
// Schedule Header
// =============================================================================

@Composable
private fun ScheduleHeader(
    activeFieldTab: FieldTab,
    onFieldTabSelected: (FieldTab) -> Unit,
    pendingCount: Int,
    calendarBookings: List<BookingDto>,
    onNavigateWeek: (Int) -> Unit,
    onGoToThisWeek: () -> Unit
) {
    val t = LocalAppStrings.current
    // Compute status counts from real bookings
    val inProgressCount = calendarBookings.count { it.status.uppercase() == "UNPAID" }
    val upcomingCount = calendarBookings.count { it.status.uppercase() == "CONFIRMED" }
    val completedCount = calendarBookings.count { it.status.uppercase() == "COMPLETED" }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding", "0 ${s(12)}")
                property("justify-content", "space-between")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left group: Title + This Week + Arrows + Status Indicators
        Row(
            modifier = Modifier.styleModifier { property("gap", s(40)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Title
            SpanText(
                t.dashboardArenaSchedule,
                modifier = Modifier
                    .sFontSize(26)
                    .fontWeight(AppTypography.SemiBold)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                    .styleModifier { property("letter-spacing", "0.26px") }
            )

            // "This Week" pill + navigation arrows
            Row(
                modifier = Modifier.styleModifier { property("gap", s(8)) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .sHeight(44)
                        .sBorderRadius(22)
                        .cursor(Cursor.Pointer)
                        .onClick { onGoToThisWeek() }
                        .styleModifier {
                            property("padding", "${s(8)} ${s(22)}")
                            property("background-color", "#0061FF")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.dashboardThisWeek,
                        modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                    )
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .sSize(44)
                            .cursor(Cursor.Pointer)
                            .onClick { onNavigateWeek(-1) }
                            .styleModifier {
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.ChevronForward,
                            modifier = Modifier.sSize(24).styleModifier {
                                property("transform", "rotate(180deg)")
                                property("filter", AppColors.IconDefaultFilter)
                            }
                        )
                    }
                    Box(
                        modifier = Modifier
                            .sSize(44)
                            .cursor(Cursor.Pointer)
                            .onClick { onNavigateWeek(1) }
                            .styleModifier {
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.ChevronForward,
                            modifier = Modifier.sSize(24).styleModifier {
                                property("filter", AppColors.IconDefaultFilter)
                            }
                        )
                    }
                }
            }

            // Status indicators row
            Row(
                modifier = Modifier.styleModifier { property("gap", s(8)) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                StatusIndicator(variant = StatusVariant.InProgress, count = inProgressCount)
                StatusIndicator(variant = StatusVariant.Upcoming, count = upcomingCount)
                StatusIndicator(variant = StatusVariant.Pending, count = pendingCount)
                StatusIndicator(variant = StatusVariant.Completed, count = completedCount)
            }
        }

        // Right group: Field switcher + Add Event
        Row(
            modifier = Modifier.styleModifier { property("gap", s(40)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            FieldSwitcher(
                activeTab = activeFieldTab,
                onTabSelected = onFieldTabSelected
            )

            Box(
                modifier = Modifier
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("position", "relative")
                    }
            ) {
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("background-color", "#0061FF")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("flex-shrink", "0")
                            property("z-index", "1")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = AppIcons.Add,
                        modifier = Modifier.sSize(24).styleModifier {
                            property("filter", "brightness(0) invert(1)")
                        }
                    )
                }
                Box(
                    modifier = Modifier
                        .sHeight(44)
                        .sBorderRadius(22)
                        .styleModifier {
                            property("padding", "0 ${s(24)}")
                            property("background-color", "#0061FF")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("margin-left", s(-4))
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.dashboardAddEvent,
                        modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                    )
                }
            }
        }
    }
}

// =============================================================================
// Schedule Grid
// =============================================================================

@Composable
private fun ScheduleGrid(
    calendar: BookingCalendarDto?,
    fields: List<FieldDto>,
    weekDayLabels: List<String>,
    activeFieldTab: FieldTab,
    selectedBookingId: String?,
    onBookingClick: (BookingDto) -> Unit,
    onClosePopup: () -> Unit,
    onApproveBooking: (String) -> Unit,
    onRejectBooking: (String) -> Unit,
    onMarkPaid: (String) -> Unit,
    onCompleteBooking: (String) -> Unit,
    onStartBooking: (String) -> Unit
) {
    val t = LocalAppStrings.current
    val gridColumns = "${s(104)} repeat(7, 1fr)"
    val days = calendar?.days ?: emptyList()

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("flex", "1 1 0")
                property("min-height", "0")
                property("overflow", "auto")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
    ) {
        // Time header row — sticky
        Div(attrs = {
            style {
                width(100.percent)
                property("display", "grid")
                property("grid-template-columns", gridColumns)
                property("flex-shrink", "0")
                property("position", "sticky")
                property("top", "0")
                property("z-index", "10")
            }
        }) {
            Div(attrs = {
                style {
                    property("height", s(44))
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", s(8))
                    property("padding-left", s(12))
                    property("background", "#060C1A")
                    property("border-top", "0.3px solid rgba(148,149,152,0.3)")
                }
            }) {
                Icon(
                    src = AppIcons.Time,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                )
                SpanText(
                    t.dashboardTimeHeader,
                    modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                )
            }

            weekDayLabels.forEach { day ->
                Div(attrs = {
                    style {
                        property("height", s(44))
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("background", "#060C1A")
                        property("border-top", "0.3px solid rgba(148,149,152,0.3)")
                    }
                }) {
                    SpanText(
                        day,
                        modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                }
            }
        }

        // Schedule body rows
        timeSlots.forEachIndexed { hourIndex, timeLabel ->
            val hour = hourIndex + 7
            val hourStr = "${hour.toString().padStart(2, '0')}:00"

            Div(attrs = {
                style {
                    width(100.percent)
                    property("display", "grid")
                    property("grid-template-columns", gridColumns)
                    property("flex-shrink", "0")
                }
            }) {
                // Time column cell
                Div(attrs = {
                    style {
                        property("height", s(76))
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("background", "#060C1A")
                    }
                }) {
                    SpanText(
                        timeLabel,
                        modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                }

                // Day slot cells
                (0..6).forEach { dayIndex ->
                    val dayData = days.getOrNull(dayIndex)
                    val bookingsInSlot = dayData?.bookings?.filter { booking ->
                        booking.startTime <= hourStr && booking.endTime > hourStr
                    }?.let { list ->
                        // Filter by field tab
                        when (activeFieldTab) {
                            FieldTab.All -> list
                            else -> {
                                val fieldIdx = FieldTab.entries.indexOf(activeFieldTab) - 1
                                val fieldId = fields.getOrNull(fieldIdx)?.id
                                if (fieldId != null) list.filter { it.fieldId == fieldId } else list
                            }
                        }
                    } ?: emptyList()

                    val booking = bookingsInSlot.firstOrNull()

                    Div(attrs = {
                        style {
                            property("height", s(76))
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("min-width", "0")
                            property("padding", "${s(4)} ${s(2)}")
                        }
                    }) {
                        if (booking != null) {
                            val bookingField = mapBookingField(booking.fieldId, fields)
                            val paymentStatus = mapPaymentStatus(booking)
                            val isSelected = selectedBookingId == booking.id

                            Box(
                                modifier = Modifier
                                    .cursor(Cursor.Pointer)
                                    .onClick { onBookingClick(booking) }
                                    .styleModifier {
                                        property("width", "100%")
                                        property("position", "relative")
                                    }
                            ) {
                                BookingCard(
                                    field = bookingField,
                                    paymentStatus = paymentStatus,
                                    customerName = booking.userName ?: t.dashboardCustomerFallback,
                                    timeFrom = booking.startTime,
                                    timeTo = booking.endTime,
                                    price = booking.totalPrice,
                                    modifier = Modifier.fillMaxWidth()
                                )
                                if (isSelected) {
                                    Box(
                                        modifier = Modifier.styleModifier {
                                            property("position", "absolute")
                                            property("left", "50%")
                                            property("top", "100%")
                                            property("transform", "translateX(-50%)")
                                            property("z-index", "100")
                                            property("box-shadow", "0px ${s(8)} ${s(24)} rgba(0, 0, 0, 0.5)")
                                        }
                                    ) {
                                        BookingDetailModal(
                                            action = paymentStatusToModalAction(paymentStatus),
                                            customerName = booking.userName ?: t.dashboardCustomerFallback,
                                            field = bookingField,
                                            bookingStatus = paymentStatusToBookingStatus(paymentStatus),
                                            paymentStatus = paymentStatus,
                                            timeFrom = booking.startTime,
                                            timeTo = booking.endTime,
                                            price = booking.totalPrice,
                                            onClose = onClosePopup,
                                            onDecline = { onRejectBooking(booking.id); onClosePopup() },
                                            onConfirm = { onApproveBooking(booking.id); onClosePopup() },
                                            onCompleteSession = { onCompleteBooking(booking.id); onClosePopup() }
                                        )
                                    }
                                }
                            }
                        } else {
                            BookingCardEmpty(modifier = Modifier.fillMaxWidth())
                        }
                    }
                }
            }
        }
    }
}

// =============================================================================
// Main Section
// =============================================================================

@Composable
fun DashboardScheduleSection(
    calendar: BookingCalendarDto? = null,
    fields: List<FieldDto> = emptyList(),
    weekDayLabels: List<String> = LocalAppStrings.current.let {
        listOf(
            it.dashboardDayMon, it.dashboardDayTue, it.dashboardDayWed, it.dashboardDayThu,
            it.dashboardDayFri, it.dashboardDaySat, it.dashboardDaySun,
        )
    },
    pendingCount: Int = 0,
    onNavigateWeek: (Int) -> Unit = {},
    onGoToThisWeek: () -> Unit = {},
    onApproveBooking: (String) -> Unit = {},
    onRejectBooking: (String) -> Unit = {},
    onMarkPaid: (String) -> Unit = {},
    onCompleteBooking: (String) -> Unit = {},
    onStartBooking: (String) -> Unit = {}
) {
    var activeFieldTab by remember { mutableStateOf(FieldTab.All) }
    var selectedBookingId by remember { mutableStateOf<String?>(null) }

    val allBookings = calendar?.days?.flatMap { it.bookings } ?: emptyList()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(18)
            .styleModifier {
                property("flex", "1 1 0")
                property("min-height", "0")
                property("background", "linear-gradient(176deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)")
                property("backdrop-filter", "blur(${s(11)})")
            }
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sBorderRadius(18)
                .styleModifier {
                    property("border", "1px solid rgba(41, 52, 138, 0.2)")
                    property("overflow", "hidden")
                    property("padding-top", s(16))
                    property("gap", s(40))
                    property("height", "100%")
                }
        ) {
            ScheduleHeader(
                activeFieldTab = activeFieldTab,
                onFieldTabSelected = { activeFieldTab = it },
                pendingCount = pendingCount,
                calendarBookings = allBookings,
                onNavigateWeek = onNavigateWeek,
                onGoToThisWeek = onGoToThisWeek
            )
            ScheduleGrid(
                calendar = calendar,
                fields = fields,
                weekDayLabels = weekDayLabels,
                activeFieldTab = activeFieldTab,
                selectedBookingId = selectedBookingId,
                onBookingClick = { booking ->
                    selectedBookingId = if (selectedBookingId == booking.id) null else booking.id
                },
                onClosePopup = { selectedBookingId = null },
                onApproveBooking = onApproveBooking,
                onRejectBooking = onRejectBooking,
                onMarkPaid = onMarkPaid,
                onCompleteBooking = onCompleteBooking,
                onStartBooking = onStartBooking
            )
        }
    }
}
